import { Component, Input } from '@angular/core';
import { ClienteUnicoAppDto, ClienteUnicoServiceProxy, OrderDto } from '../../services/service-proxy/service-proxies';
import { AppService } from '../../services/app.service';
import { claveNivel, mismoNivel, umbralesDePais } from '../../shared/niveles';
import { vencimientoDePuntos, finDeMes } from '../../shared/puntos';

@Component({
  selector: 'app-resumen-puntos',
  templateUrl: './resumen-puntos.component.html',
  styleUrls: ['./resumen-puntos.component.scss']
})
export class ResumenPuntosComponent {

  @Input() model? : ClienteUnicoAppDto | null;

  /**
   * Compras del cliente, para estimar el ahorro anual del incentivo. Llegan de
   * la home (las mismas "ultimas compras"), asincronicas: mientras no estan, el
   * incentivo se muestra sin la cifra de ahorro.
   */
  @Input() orders? : OrderDto[] | null;

  // Escalera de niveles del pais del cliente (los umbrales de AR y CO difieren).
  private get umbrales() {
    return umbralesDePais(this.appService.countryCode);
  }

  // Porcentaje de ahorro anual estimado al subir de nivel, definido por Puppis.
  // La clave es el nivel ACTUAL (desde donde se sube), normalizada con claveNivel.
  private static readonly PORCENTAJE_AHORRO : { [nivel : string] : number } = {
    'general': 0.08,     // General -> Aliado
    'aliado': 0.03,      // Aliado -> Amigo Fiel
    'amigo fiel': 0.05   // Amigo Fiel -> Fanatico
  };

  // Puntos que vencen este mes, calculados desde el historial real.
  puntosPorVencer = 0;
  finDeMesActual = finDeMes(new Date());

  constructor(private clienteUnicoService : ClienteUnicoServiceProxy,
              private appService : AppService) {}

  ngOnInit() {
    this.cargarPorVencer();
  }

  /**
   * Los puntos por vencer del mes salen del historial (fidelidad modo 2): son
   * los movimientos cuyo vencimiento cae entre hoy y el fin del mes en curso.
   * El total suelto (puntosAVencer) no dice cuando vencen, por eso se recorre
   * el detalle.
   */
  private cargarPorVencer() {

    const idUnico = this.appService.idUnico;
    if (!idUnico) return;

    const hoy = new Date();
    const hasta = this.finDeMesActual;

    this.clienteUnicoService
        .getFidelidadPointsOrder(idUnico)
        .subscribe({
          next : movimientos => {
            this.puntosPorVencer = (movimientos ?? []).reduce((suma, m) => {
              const compra = this.aFecha(m.fecha);
              if (!compra) return suma;
              const vence = vencimientoDePuntos(compra, this.appService.countryCode);
              const enElMes = vence.getTime() > hoy.getTime() && vence.getTime() <= hasta.getTime();
              return enElMes ? suma + this.aNumero(m.puntosCompra) + this.aNumero(m.puntosPesos) : suma;
            }, 0);
          },
          error : () => { /* si falla, no se muestra el aviso de por vencer */ }
        });
  }

  private aFecha(valor : string | undefined) : Date | null {
    if (!valor) return null;
    const d = new Date(valor);
    return isNaN(d.getTime()) ? null : d;
  }

  /**
   * Los numeros llegan como string con separador de miles ("12,213"). Las
   * devoluciones del historial vienen en negativo ("-1,094"): se conserva el
   * signo para que neteen bien en los puntos por vencer (los totales de modo 1
   * nunca son negativos, asi que no se ven afectados).
   */
  private aNumero(valor : string | null | undefined) : number {
    const s = String(valor ?? '').trim();
    if (!s) return 0;
    const negativo = s.startsWith('-');
    const digits = s.replace(/[^\d]/g, '');
    if (!digits) return 0;
    const n = Number(digits);
    return isNaN(n) ? 0 : (negativo ? -n : n);
  }

  get puntosTotales() : number {
    return this.aNumero(this.model?.fidelidadPointsTotal?.puntosTotales);
  }

  get puntosFaltantes() : number {
    return this.aNumero(this.model?.fidelidadPointsTotal?.puntosNextTier);
  }

  get nivelActual() : string {
    return this.model?.fidelidadPointsTotal?.nombreTier ?? '';
  }

  get estaEnNivelMasAlto() : boolean {
    return this.puntosFaltantes === 0 && this.puntosTotales > 0;
  }

  get hayPorVencer() : boolean {
    return this.puntosPorVencer > 0;
  }

  /**
   * El nombre del nivel siguiente NO viene en la API: solo llega el actual.
   * Se deduce recorriendo la escalera de niveles.
   */
  get nivelSiguiente() : string | null {
    const umbrales = this.umbrales;
    const i = umbrales.findIndex(t => mismoNivel(t.nombre, this.nivelActual));
    if(i < 0 || i === umbrales.length - 1) return null;
    return umbrales[i + 1].nombre;
  }

  get umbralActual() : number {
    return this.umbrales.find(t => mismoNivel(t.nombre, this.nivelActual))?.desde ?? 0;
  }

  get umbralSiguiente() : number {
    return this.umbrales.find(t => mismoNivel(t.nombre, this.nivelSiguiente))?.desde ?? 0;
  }

  /** Porcentaje de avance dentro del nivel actual, acotado a 0-100. */
  get porcentajeAvance() : number {
    if(this.estaEnNivelMasAlto) return 100;

    const desde = this.umbralActual;
    const hasta = this.umbralSiguiente;
    if(hasta <= desde) return 0;

    const avance = ((this.puntosTotales - desde) / (hasta - desde)) * 100;
    return Math.max(0, Math.min(100, Math.round(avance)));
  }

  // ── Incentivo al nivel siguiente ────────────────────────────────────────────

  /**
   * Ahorro anual estimado al subir de nivel. Se anualiza la venta a partir de
   * las compras que tenemos (promedio mensual por los meses con datos, x12) y se
   * aplica el porcentaje del nivel (ver PORCENTAJE_AHORRO). Devuelve null si no
   * hay compras o si el nivel actual no tiene porcentaje (p. ej. Fanatico, que
   * ya es el mas alto).
   */
  get ahorroAnualEstimado() : number | null {

    const porcentaje = ResumenPuntosComponent.PORCENTAJE_AHORRO[claveNivel(this.nivelActual)];
    if (!porcentaje) return null;

    const compras = this.orders ?? [];
    if (!compras.length) return null;

    // Sumo los montos y cuento cuantos meses distintos hay con compras.
    // sourceCreationTime es moment.Moment, asi que se pasa a Date con toDate().
    const porMes = new Map<string, number>();
    for (const o of compras) {
      const fecha = o.sourceCreationTime ? o.sourceCreationTime.toDate() : null;
      if (!fecha || isNaN(fecha.getTime())) continue;
      const clave = `${fecha.getFullYear()}-${fecha.getMonth()}`;
      porMes.set(clave, (porMes.get(clave) ?? 0) + (o.grossAmount ?? 0));
    }

    const meses = porMes.size;
    if (!meses) return null;

    const totalVentas = Array.from(porMes.values()).reduce((a, b) => a + b, 0);
    const ventaAnualizada = (totalVentas / meses) * 12;

    return Math.round(ventaAnualizada * porcentaje);
  }
}
